import { Controller } from '@hotwired/stimulus'

// Destructive-action confirm for the admin zone (E7). Progressive enhancement:
// the form submits server-side without JS; with JS it asks for confirmation
// first and, once confirmed, gives the submit a pending label — the disable /
// urgent takedown runs synchronously (inline file removal), so the button
// should show progress rather than sit inert. When an "urgent" checkbox target
// is present and checked, the stronger urgent message is used instead — so a
// normal disable and an irreversible urgent takedown never share the same
// warning (KTD5).
export default class extends Controller {
  static values = {
    message: String,
    urgentMessage: String,
    pendingLabel: String,
  }
  static targets = ['urgent']

  declare readonly messageValue: string
  declare readonly urgentMessageValue: string
  declare readonly pendingLabelValue: string
  declare readonly hasPendingLabelValue: boolean
  declare readonly hasUrgentTarget: boolean
  declare readonly urgentTarget: HTMLInputElement

  connect() {
    // Sync marker so a system test can wait until the submit handler is wired
    // before clicking — otherwise the click can beat Stimulus and skip the dialog.
    this.element.setAttribute('data-confirm-ready', 'true')
  }

  confirm(event: Event) {
    const useUrgent =
      this.hasUrgentTarget &&
      this.urgentTarget.checked &&
      this.urgentMessageValue.length > 0
    const message = useUrgent ? this.urgentMessageValue : this.messageValue

    if (message && !window.confirm(message)) {
      event.preventDefault()
      return
    }

    // Confirmed (or no message): the form is submitting now — relabel the submit
    // so the synchronous takedown shows progress.
    this.markPending()
  }

  private markPending() {
    if (!this.hasPendingLabelValue || !this.pendingLabelValue) return
    const submit = this.element.querySelector<HTMLElement>('[type="submit"]')
    if (submit instanceof HTMLInputElement)
      submit.value = this.pendingLabelValue
    else if (submit) submit.textContent = this.pendingLabelValue
  }
}
